import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUp, X } from 'lucide-react';
import { STATUS_COLORS } from './modeIcons';
import { availableGrowingStatusGroups, growingStatusColor } from './growingStatuses';
import { VesselGlyph, ModeGlyph, PinGlyph, SwatchStack } from './legendGlyphs';
import { useTheme } from '../../../context/ThemeContext';

/**
 * Metrics from the design (Figma 55333:6939). That frame is drawn at 1.35x —
 * its 9.45px swatch and 0.756px tracking are a 7px swatch and 0.56px tracking
 * scaled up — so the values below are the frame's divided back down, not what
 * the layer panel reports.
 */
const PANEL_W = 'w-[232px]';
const HEADING = "font-['Inter'] text-[8px] font-extrabold uppercase tracking-[0.56px] text-[#9CA3AF] dark:text-[#6B716C]";
const LABEL = "font-['Inter'] text-[10.5px] leading-[14px] whitespace-nowrap text-[#111827] dark:text-[#E6E8E6]";
const NOTE = "font-['Inter'] text-[9.5px] text-[#9CA3AF] dark:text-[#828382]";
const GLYPH = 'text-[#5D625F] dark:text-[#9AA09A]';
const DIVIDER = 'bg-[#EEF0EC] dark:bg-[#2A302C]';

/**
 * At-port and discharged were dropped by an earlier review. Their colours stay
 * in modeIcons because rows still carry those states; they are simply not
 * keyed here or offered in the status filter.
 */
const STATUS_ROWS = ['on_time', 'ahead', 'delayed'];
const STATUS_FALLBACKS = { on_time: 'On time', ahead: 'Ahead', delayed: 'Delayed' };

/** One mode row: glyph, name, and the quiet qualifier beside it. */
const ModeRow = ({ children, label, note }) => (
  <div className="flex min-w-0 items-center gap-[7px]">
    <span className={`flex h-[14px] w-[14px] shrink-0 items-center justify-center ${GLYPH}`}>{children}</span>
    <span className={LABEL}>
      {label}
      {note ? <span className={NOTE}>{` ${note}`}</span> : null}
    </span>
  </div>
);

const Section = ({ title, children, first = false }) => (
  <>
    {!first && <div className={`my-[9px] h-px w-full ${DIVIDER}`} />}
    <p className={HEADING}>{title}</p>
    <div className="mt-[7px]">{children}</div>
  </>
);

/**
 * The map's combined key: what the marker shapes mean, what the shipment
 * colours mean, and what the pin colours mean — in one panel.
 *
 * It replaces the pair of single-layer legends that used to sit in this corner
 * on All, where two keys abreast read as one wide object and neither said what
 * the other covered.
 *
 * Every colour is read from the table the map itself draws from — shipment
 * states from `modeIcons`, regions from `growingStatuses` — so the key cannot
 * describe a palette the map is not using. That matters more than matching the
 * mock swatch-for-swatch: the design's Ahead and Delayed chips are a different
 * teal and red from the markers', and a legend that disagrees with its own
 * pins is worse than one a shade off the comp.
 *
 * @param {boolean} showModes    Include the Mode and Shipment status sections.
 * @param {boolean} showRegions  Include the Region status section.
 */
const MapLegend = ({ className = '', defaultOpen = false, showModes = true, showRegions = true }) => {
  // Closed by default: the map is the content, and a key the user has not
  // asked for should not cover it on arrival.
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [open, setOpen] = useState(defaultOpen);
  const label = t('map.legend.legend', 'Legend');

  // The pill's swatches hint at whichever sections the panel actually has.
  const swatches = [
    ...(showModes ? STATUS_ROWS.map((id) => STATUS_COLORS[id]) : []),
    ...(showRegions ? ['producing', 'starting_soon'].map(growingStatusColor) : []),
  ].slice(0, 3);

  return (
    <div className={`flex flex-col items-start gap-[4px] ${className}`}>
      {open && (
        <div
          className={`relative ${PANEL_W} rounded-[12px] border border-solid border-[#E6E9E4] bg-white px-[12px] py-[12px] drop-shadow-[0px_8px_11px_rgba(17,24,39,0.15)] dark:border-[#2A302C] dark:bg-[#1B1F1C]`}
        >
          {/* Closing from the panel as well as the pill: at 232px the panel
              covers a corner of the map, and reaching past it to the pill
              underneath to get rid of it is the long way round. */}
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={t('common.close', 'Close')}
            className="absolute right-[8px] top-[8px] flex size-[16px] cursor-pointer items-center justify-center rounded-full text-[#9CA3AF] transition-colors hover:text-[#343440] dark:hover:text-[#E6E8E6]"
          >
            <X size={12} />
          </button>

          {showModes && (
            <>
              <Section first title={t('map.legend.mode', 'Mode')}>
                {/* Two columns, as the design has it: four rows stacked would
                    make this panel twice as tall for the same four words. */}
                <div className="grid grid-cols-2 gap-x-[8px] gap-y-[7px]">
                  <ModeRow label={t('map.legend.vessel', 'Vessel')} note={t('map.legend.vesselNote', 'sea')}>
                    <VesselGlyph className="h-[14px] w-[14px]" />
                  </ModeRow>
                  <ModeRow label={t('map.legend.truck', 'Truck')} note={t('map.legend.truckNote', 'inland')}>
                    <ModeGlyph mode="truck" className="h-[14px] w-[14px]" />
                  </ModeRow>
                  <ModeRow label={t('map.legend.air', 'Air')}>
                    <ModeGlyph mode="air" className="h-[14px] w-[14px]" />
                  </ModeRow>
                  <ModeRow label={t('map.legend.inPort', 'In port')} note={t('map.legend.inPortNote', 'waiting')}>
                    <img
                      src={isDark ? '/assets/icons/box-dark.svg' : '/assets/icons/box-light.svg'}
                      alt=""
                      aria-hidden="true"
                      className="h-[14px] w-[14px]"
                    />
                  </ModeRow>
                </div>
              </Section>

              <Section title={t('map.legend.shipmentStatus', 'Shipment status')}>
                {/* One line of three: they are a single scale (early, on time,
                    late), and a column would read as three unrelated keys. */}
                <div className="flex flex-wrap items-center gap-x-[14px] gap-y-[6px]">
                  {STATUS_ROWS.map((id) => (
                    <div key={id} className="flex items-center gap-[6px]">
                      <span
                        className="size-[7px] shrink-0 rounded-full"
                        style={{ backgroundColor: STATUS_COLORS[id] }}
                      />
                      <span className={LABEL}>
                        {t(`map.filters.shipmentStatus.${id}`, STATUS_FALLBACKS[id])}
                      </span>
                    </div>
                  ))}
                </div>
              </Section>
            </>
          )}

          {showRegions && (
            <Section first={!showModes} title={t('map.growing.legendTitle', 'Region status')}>
              {/* Flattened across the status groups: the divider those groups
                  carry belongs to the filter list, where the two runs mean
                  something. Here it would read as a fourth section. */}
              <div className="grid grid-cols-2 gap-x-[8px] gap-y-[6px]">
                {availableGrowingStatusGroups().flat().map((status) => (
                  <div key={status.id} className="flex min-w-0 items-center gap-[6px]">
                    <PinGlyph color={status.color} isDark={isDark} className="h-[15px] w-[11px]" />
                    <span className={LABEL}>
                      {t(`map.filters.productionStatus.${status.id}`, status.label)}
                    </span>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      )}

      {/* Toggle. Last in DOM order so it stays put as the panel opens upward. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex h-[28px] w-[117px] items-center rounded-[14px] border border-solid border-[#E6E9E4] bg-white pl-[10px] pr-[8px] drop-shadow-[0px_1px_1.5px_rgba(17,24,39,0.07)] dark:border-[#2A302C] dark:bg-[#1B1F1C]"
      >
        <span className="font-['Inter'] text-[10px] font-semibold whitespace-nowrap text-[#343440] dark:text-[#E6E8E6]">
          {label}
        </span>
        <SwatchStack colors={swatches} />
        <ChevronUp
          size={10}
          className={`shrink-0 text-[#5D625F] transition-transform dark:text-[#9AA09A] ${open ? '' : 'rotate-180'}`}
        />
      </button>
    </div>
  );
};

export default MapLegend;
